import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { 
    Container, Row, Col, Card, Spinner, Badge, Button, Stack, 
    Accordion, Tabs, Tab, Table
} from 'react-bootstrap';
import { Joyride, STATUS } from 'react-joyride';
import { Users, ShieldCheck, Zap, Layout, BookOpen, FileText, Link2, BarChart2, Palette } from 'lucide-react';
import { useAccordionButton } from 'react-bootstrap/AccordionButton';

import AppFooter from '../components/Common/AppFooter';
import ProgramTable from '../components/TenantAdmin/ProgramTable';
import ProjectTable from '../components/TenantAdmin/ProjectTable';
import TenantAuditLogTable from '../components/TenantAdmin/TenantAuditLogTable';
import TenantUserManagement from '../components/TenantAdmin/TenantUserManagement';
import BulkFacultyManager from '../components/TenantAdmin/BulkFacultyManager';
import ContentStandardManager from '../components/TenantAdmin/ContentStandardManager';
import TenantMoodleSettings from '../components/TenantAdmin/TenantMoodleSettings';
import TenantSubscriptionAnalytics from '../components/TenantAdmin/TenantSubscriptionAnalytics';
import TenantBrandingPanel from '../components/TenantAdmin/TenantBrandingPanel';

import api, {
    getTenantDashboardMetrics,
    getTenantPrograms,
    getTenantProjects,
    getTenantAuditLogs,
    getTenantModules,
} from '../api/api';
import { setTenantModules } from '../redux/slices/userSlice';

// Safe Accordion Toggle
function CustomToggle({ children, eventKey, disabled }) {
    const decoratedOnClick = useAccordionButton(eventKey, (e) => {
        e.preventDefault();
        e.stopPropagation();
    });
    return (
        <Button variant="outline-dark" size="sm" className="rounded-pill px-3 fw-bold" onClick={decoratedOnClick} disabled={disabled}>
            {children}
        </Button>
    );
}

const TenantAdminDashboard = () => {
    const { slug: tenantSlug } = useParams();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { branding } = useSelector((state) => state.user || {});
    const tenantModules = useSelector((state) => state.user?.tenantModules || ["examination"]);
    const hasModule = (m) => tenantModules.includes(m);
    const isCopilotTenant = hasModule('teaching_copilot');  // drives tab visibility
    
    const [metrics, setMetrics] = useState(null);
    const [faculty, setFaculty] = useState([]);
    const [programs, setPrograms] = useState([]);
    const [projects, setProjects] = useState([]);
    const [auditLogs, setAuditLogs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [wallet, setWallet] = useState(null);

    const [runTour, setRunTour] = useState(false);
    const [steps] = useState([
        { target: '.admin-metrics-row', content: 'Monitor institutional health.', placement: 'bottom', disableBeacon: true },
        { target: '.project-registry-card', content: 'Manage the production pipeline.', placement: 'top' },
        { target: '.user-management-card', content: 'Set up Access Governance.', placement: 'top' }
    ]);

    const loadData = useCallback(async () => {
        try {
            setLoading(true);
            const [metricsRes, programsRes, projectsRes, auditRes, walletRes, facultyRes] = await Promise.all([
                getTenantDashboardMetrics(tenantSlug),
                getTenantPrograms(tenantSlug),
                getTenantProjects(tenantSlug),
                getTenantAuditLogs(tenantSlug),
                api.get(`/tenant/${tenantSlug}/wallet/balance`),
                api.get(`/tenant/${tenantSlug}/admin/faculty-roster`)
            ]);
            
            setMetrics(metricsRes.data);
            setPrograms(programsRes.data || []);
            setProjects(projectsRes.data || []);
            setAuditLogs(auditRes.data || []);
            setWallet(walletRes.data);
            setFaculty(facultyRes.data || []);
            // Fetch + cache tenant modules
            try {
                const modRes = await getTenantModules(tenantSlug);
                dispatch(setTenantModules(modRes.modules?.length ? modRes.modules : ["examination"]));
            } catch { dispatch(setTenantModules(["examination"])); }
        } catch (err) {
            console.error("Failed to load dashboard", err);
        } finally {
            setLoading(false);
        }
    }, [tenantSlug]);

    useEffect(() => { if (tenantSlug) loadData(); }, [loadData, tenantSlug]);

    // Made safer to handle different casing in the DB
    const isStageReached = (project, stage) => {
        if (!project || !project.current_stage) return false;
        const stageOrder = ['curriculum', 'curriculum_mapping', 'blueprint', 'ckb_completed', 'orchestration'];
        return stageOrder.indexOf(project.current_stage.toLowerCase()) >= stageOrder.indexOf(stage);
    };

    return (
        <div className="bg-light min-vh-100 d-flex flex-column">
            <Joyride steps={steps} run={runTour} continuous={true} showSkipButton={true} callback={(d) => [STATUS.FINISHED, STATUS.SKIPPED].includes(d.status) && setRunTour(false)} styles={{ options: { primaryColor: '#0d6efd', zIndex: 10000 } }} />

            
            <Container fluid className="px-4 py-4 flex-grow-1">
                {/* Page header — landmark + breadcrumb */}
                <header className="mb-5 d-flex justify-content-between align-items-start" role="banner">
                    <div className="d-flex align-items-center gap-3">
                        <div className="p-3 bg-white rounded-4 shadow-sm" aria-hidden="true">
                            <ShieldCheck size={32} className="text-primary" />
                        </div>
                        <div>
                            <h1 className="h3 fw-bold text-dark mb-0">Institutional Command Center</h1>
                            <p className="text-muted mb-0" aria-label="Institution name">
                                {branding?.institutionName || tenantSlug?.toUpperCase()}
                            </p>
                        </div>
                    </div>
                    <Stack direction="horizontal" gap={2}>
                        <Button
                            variant="outline-secondary"
                            className="btn-pill"
                            onClick={loadData}
                            aria-label="Refresh dashboard data"
                        >
                            <span aria-hidden="true">🔄</span> Refresh
                        </Button>
                        <Button
                            variant="primary"
                            className="btn-pill"
                            onClick={() => setRunTour(true)}
                            aria-label="Start guided tour"
                        >
                            <span aria-hidden="true">💡</span> Guide
                        </Button>
                    </Stack>
                </header>

                {/* KPI stat cards — accessible description list pattern */}
                <Row className="mb-5 admin-metrics-row g-3" role="region" aria-label="Key metrics">
                    {[
                        { label: 'Wallet Credits', value: wallet?.balance?.toFixed(2) || '0.00', colour: 'success' },
                        { label: 'Faculty Roster', value: faculty?.length || 0,               colour: 'primary' },
                        { label: 'Live Projects',  value: metrics?.projects?.total || 0,      colour: 'info' },
                        { label: 'Pending Reviews',value: metrics?.reviews?.pending || 0,     colour: 'warning' },
                    ].map(({ label, value, colour }) => (
                        <Col md={3} key={label}>
                            <dl className={`stat-card border-start border-4 border-${colour} text-center mb-0`}>
                                <dt className="stat-card__label">{label}</dt>
                                <dd className={`stat-card__value text-${colour} mb-0`}>{value}</dd>
                            </dl>
                        </Col>
                    ))}
                </Row>

                <Tabs defaultActiveKey={isCopilotTenant ? "production" : "governance"} className="mb-4" aria-label="Admin sections">
                    {isCopilotTenant && (
                    <Tab eventKey="production" title={<span><Layout size={18} className="me-2" aria-hidden="true" />Production Pipeline</span>}>
                        
                        {/* PROJECT REGISTRY IS HERE */}
                        <Card className="shadow-sm border-0 mb-4 rounded-4 project-registry-card">
                            <Card.Header className="bg-white py-3 d-flex justify-content-between align-items-center border-bottom">
                                <h6 className="mb-0 fw-bold text-primary text-uppercase ls-1">Project Registry & SME Assignment</h6>
                            </Card.Header>
                            <Card.Body className="p-0">
                                <ProjectTable projects={projects} programs={programs} tenantSlug={tenantSlug} onRefresh={loadData} />
                            </Card.Body>
                        </Card>

                        {/* LIVE ORCHESTRATION ACCORDION */}
                        <div className="mt-4">
                            <h6 className="fw-bold text-muted uppercase x-small mb-3">Live Orchestration Status</h6>
                            {loading ? <div className="text-center py-4"><Spinner animation="border" aria-hidden="true" /></div> : (
                                <Accordion className="border-0">
                                    {projects.map((project, pIdx) => {
                                        const programName = programs.find(p => p.id === project.program_id)?.name || "Unmapped";
                                        return (
                                            <Card key={project.id} className="mb-2 border-0 shadow-sm rounded-3 overflow-hidden">
                                                <Card.Body className="py-2 px-3">
                                                    <Row className="align-items-center">
                                                        <Col md={4}>
                                                            <div className="fw-bold small">{project.name}</div>
                                                            <div className="x-small text-muted">{programName}</div>
                                                        </Col>
                                                        <Col md={2} className="text-center">
                                                            <Badge bg="light" text="dark" className="border text-capitalize">{project.current_stage?.replace("_", " ")}</Badge>
                                                        </Col>
                                                        <Col md={6} className="text-end">
                                                            <Stack direction="horizontal" gap={2} className="justify-content-end">
                                                                {/* Restored Academic Profile */}
                                                                <Button variant="outline-primary" size="sm" className="rounded-pill"
                                                                    disabled={!isStageReached(project, 'curriculum_mapping')}
                                                                    onClick={() => navigate(`/tenant/${tenantSlug}/projects/${project.id}/academic-profile`)}>
                                                                    🎓 Profile
                                                                </Button>

                                                                {/* Restored Graph Button */}
                                                                <Button variant="outline-dark" size="sm" className="rounded-pill"
                                                                    disabled={project.ckb_status !== 'completed'}
                                                                    onClick={() => navigate(`/graph/${project.id}`)}>
                                                                    📊 Graph
                                                                </Button>

                                                                {/* Academic Evidence Dashboards */}
                                                                <Button variant="outline-success" size="sm" className="rounded-pill"
                                                                    disabled={!isStageReached(project, 'ckb_completed')}
                                                                    onClick={() => navigate(`/tenant/${tenantSlug}/projects/${project.id}/evidence`)}>
                                                                    🔬 Evidence
                                                                </Button>

                                                                {/* Custom Toggle fixes the open/close action */}
                                                                <CustomToggle eventKey={String(pIdx)} disabled={!isStageReached(project, 'orchestration')}>
                                                                    ✏️ View Units
                                                                </CustomToggle>
                                                            </Stack>
                                                        </Col>
                                                    </Row>
                                                    <Accordion.Collapse eventKey={String(pIdx)}>
                                                        <div className="mt-3 p-3 bg-light rounded border">
                                                            <Table size="sm" borderless responsive className="mb-0 small">
                                                                <thead><tr className="text-muted uppercase x-small"><th>Unit</th><th>Status</th><th>Review Action</th></tr></thead>
                                                                <tbody>
                                                                    {/* Map through the curriculum draft to build the view editor buttons */}
                                                                    {(project.curriculum_draft || []).flatMap(m => m.children || []).map((unit, uIdx) => {
                                                                        const unitId = unit.unit_id || unit.id || `u-${uIdx}`;
                                                                        const status = project.orchestration_progress?.[unitId] || 'pending';
                                                                        const isReady = status === 'completed' || status === 'partial_success';
                                                                        return (
                                                                            <tr key={uIdx}>
                                                                                <td className="fw-bold">{unit.title}</td>
                                                                                <td><Badge bg={status === 'completed' ? 'success' : 'secondary'} className="x-small">{status}</Badge></td>
                                                                                <td>
                                                                                    <Button variant="link" size="sm" className="p-0 fw-bold" 
                                                                                        disabled={!isReady}
                                                                                        onClick={() => navigate(`/editor/${project.id}/${unitId}`)}>
                                                                                        Open Editor →
                                                                                    </Button>
                                                                                </td>
                                                                            </tr>
                                                                        )
                                                                    })}
                                                                </tbody>
                                                            </Table>
                                                        </div>
                                                    </Accordion.Collapse>
                                                </Card.Body>
                                            </Card>
                                        );
                                    })}
                                </Accordion>
                            )}
                        </div>
                    </Tab>
                    )}

                    <Tab eventKey="governance" title={<span><Users size={18} className="me-2"/>Access Management</span>}>
                        <div className="mt-3 user-management-card"><TenantUserManagement tenantSlug={tenantSlug} onChanged={loadData} /></div>
                        <div className="mt-4 faculty-mapping-card"><BulkFacultyManager slug={tenantSlug} allFaculty={faculty} allProjects={projects} onComplete={loadData} /></div>
                    </Tab>

                    {isCopilotTenant && (
                    <Tab eventKey="content-standards" title={<span><FileText size={18} className="me-2" aria-hidden="true" />Content Standards</span>}>
                        <div className="mt-3">
                            <ContentStandardManager tenantSlug={tenantSlug} onStandardCreated={loadData} />
                        </div>
                    </Tab>
                    )}

                    {isCopilotTenant && (
                    <Tab eventKey="programs" title={<span><BookOpen size={18} className="me-2"/>Academic Programs</span>}>
                        <Card className="shadow-sm border-0 mt-3"><ProgramTable programs={programs} tenantSlug={tenantSlug} onRefresh={loadData} /></Card>
                    </Tab>
                    )}

                    <Tab eventKey="analytics" title={<span><BarChart2 size={18} className="me-2"/>Subscription Analytics</span>}>
                        <div className="mt-3">
                            <TenantSubscriptionAnalytics tenantSlug={tenantSlug} />
                        </div>
                    </Tab>

                    <Tab eventKey="branding" title={<span><Palette size={18} className="me-2"/>Branding</span>}>
                        <div className="mt-3">
                            <TenantBrandingPanel tenantSlug={tenantSlug} />
                        </div>
                    </Tab>

                    <Tab eventKey="audit" title={<span><Zap size={18} className="me-2"/>Activity Logs</span>}>
                        <div className="mt-3"><TenantAuditLogTable logs={auditLogs} /></div>
                    </Tab>

                    {isCopilotTenant && (
                    <Tab eventKey="integrations" title={<span><Link2 size={18} className="me-2"/>Integrations</span>}>
                        <div className="mt-3">
                            <div className="mb-4">
                                <h6 className="fw-bold text-dark mb-1">Integrations &amp; External Services</h6>
                                <p className="text-muted small mb-0">
                                    Configure third-party platform connections for this tenant. Settings here apply across all AcademicOS products including ExaminationOS and Teaching Copilot.
                                </p>
                            </div>
                            <div className="mb-2 text-uppercase x-small fw-bold text-muted ls-1">Moodle LMS</div>
                            <TenantMoodleSettings tenantSlug={tenantSlug} />
                        </div>
                    </Tab>
                    )}
                </Tabs>
            </Container>
            <AppFooter />
        </div>
    );
};

export default TenantAdminDashboard;